import { defineStore } from "pinia"
import { type Category } from "~/models/category"
import { type ApiResponse } from "~/types/api"

export const useCategoryStore = defineStore("category", () => {
	const app = useNuxtApp()

	const state = {
		categories: ref<Category[] | null>(null)
	}

	const types = computed(() => [
		{
			name: app.$i18n.t("careers"),
			name2: app.$i18n.t("careers"),
			name3: app.$i18n.t("career"),
			value: "career",
			maxLevel: 1,
			hasCategory: true,
			icon: "mdi:briefcase",
			color: "#1976D2"
		},
		{
			name: app.$i18n.t("organization_units"),
			name2: app.$i18n.t("organization_units"),
			name3: app.$i18n.t("organization_unit"),
			value: "organization-unit",
			maxLevel: 1000,
			hasCategory: true,
			icon: "mdi:office-building",
			color: "#388E3C"
		},
		{
			name: app.$i18n.t("commitments"),
			name2: app.$i18n.t("commitment"),
			name3: app.$i18n.t("commitment"),
			value: "penalty",
			maxLevel: 2,
			hasCategory: true,
			icon: "mdi:alert-circle",
			color: "#E53935"
		},
		{
			name: app.$i18n.t("foods"),
			name2: app.$i18n.t("foods"),
			name3: app.$i18n.t("food"),
			value: "food",
			maxLevel: 1,
			hasCategory: true,
			icon: "mdi:food",
			color: "#F57C00"
		}
	])

	const productOptions = computed(
		() =>
			state.categories.value
				?.filter((c) => c.type == "product-options" && !c.parent_id)
				?.map((c) => ({
					parent: { ...c, label: c.title[app.$i18n.locale.value] || c.title.en },
					children:
						state.categories.value
							?.filter((ch) => ch.type == "product-options" && ch.parent_id == c.id)
							?.map((ch) => ({
								...ch,
								label: ch.title[app.$i18n.locale.value] || ch.title.en
							})) || []
				})) || []
	)

	const createCategory = async (formData: CreateCategoryRequest) => {
		const companyStore = useCompanyStore()
		const data = await $fetch<CreateCategoryResponse>(CreateCategory.url(), {
			...useFetchConfig(),
			method: CreateCategory.method,
			body: formData
		})
		if (state.categories.value) {
			state.categories.value = [...state.categories.value, data.data]
			if (
				companyStore.company?.id &&
				companyStore.company.id == data.data.company_id &&
				data.data.type == "organization-unit" &&
				companyStore.company.organization_units
			) {
				companyStore.company.organization_units = [...companyStore.company.organization_units, data.data]
			}
		}
		return data.data
	}

	const editCategory = async (formData: EditCategoryRequest) => {
		const companyStore = useCompanyStore()
		const data = await $fetch<EditCategoryResponse>(EditCategory.url(), {
			...useFetchConfig(),
			method: EditCategory.method,
			body: formData
		})
		if (state.categories.value) {
			state.categories.value = state.categories.value.map((v) => (v.id === data.data.id ? data.data : v))
			if (
				companyStore.company?.id &&
				companyStore.company.id == data.data.company_id &&
				data.data.type == "organization-unit" &&
				companyStore.company.organization_units
			) {
				companyStore.company.organization_units = companyStore.company.organization_units.map((v) =>
					v.id === data.data.id ? data.data : v
				)
			}
		}
		return data.data
	}

	const deleteCategory = async (formData: DeleteCategoryRequest) => {
		const companyStore = useCompanyStore()
		const data = await $fetch<DeleteCategoryResponse>(DeleteCategory.url(formData.id), {
			...useFetchConfig(),
			method: DeleteCategory.method,
			body: formData
		})
		if (state.categories.value) {
			state.categories.value = state.categories.value.filter((v) => v.id !== formData.id)
			if (
				companyStore.company?.id &&
				companyStore.company.id == formData.company_id &&
				companyStore.company.organization_units
			) {
				companyStore.company.organization_units = companyStore.company.organization_units.filter(
					(v) => v.id !== formData.id
				)
			}
		}
		return data.data
	}

	const searchCategories = async (formData: SearchCategoriesRequest) => {
		const data = await $fetch<SearchCategoriesResponse>(SearchCategories.url(), {
			...useFetchConfig(),
			method: SearchCategories.method,
			query: formData
		})
		return data.data
	}

	const getCategory = async (id: number) => {
		const data = await $fetch<GetCategoryResponse>(GetCategory.url(id), {
			...useFetchConfig(),
			method: GetCategory.method
		})
		return data.data
	}

	const createCategoryExcel = async (params: CreateCategoryExcelRequest) => {
		const companyStore = useCompanyStore()
		const formData = new FormData()
		Object.keys(params).forEach((k) => formData.set(k, params[k]))
		const r = await $fetch<CreateCategoryExcelResponse>(CreateCategoryExcel.url(), {
			...useFetchConfig(),
			method: CreateCategoryExcel.method,
			body: formData
		})
		if (
			params.company_id &&
			params.type == "organization-unit" &&
			r.data.organization_units &&
			params.company_id == companyStore.company?.id
		) {
			companyStore.company = {
				...companyStore.company,
				organization_units: r.data.organization_units
			}
		}
		return r.data
	}

	const updateOrganizationUnitSupervisors = async (formData: { company_id: number }) => {
		const data = await $fetch<
			ApiResponse & {
				data: {
					updated: number
				}
			}
		>("categories/update-organization-unit-supervisors", {
			...useFetchConfig(),
			method: "POST",
			body: formData
		})
		return data.data
	}

	const deleteUnusedOrganizationUnits = async (formData: { company_id: number }) => {
		const data = await $fetch<
			ApiResponse & {
				data: {
					deleted: number
				}
			}
		>("categories/delete-unused-organization-units", {
			...useFetchConfig(),
			method: "POST",
			body: formData
		})
		return data.data
	}

	const updateCategoryNum = async (formData: UpdateCategoryNumRequest) => {
		const data = await $fetch<UpdateCategoryNumResponse>(UpdateCategoryNum.url(formData.id), {
			...useFetchConfig(),
			method: UpdateCategoryNum.method,
			body: formData
		})
		return data.data
	}

	const batchUpdateCategoryNums = async (formData: BatchUpdateCategoryNumsRequest) => {
		await $fetch<ApiResponse>(BatchUpdateCategoryNums.url(), {
			...useFetchConfig(),
			method: BatchUpdateCategoryNums.method,
			body: { ...formData, items: JSON.stringify(formData.items) }
		})
	}

	return {
		...state,
		types,
		productOptions,
		createCategory,
		editCategory,
		deleteCategory,
		searchCategories,
		getCategory,
		createCategoryExcel,
		updateOrganizationUnitSupervisors,
		deleteUnusedOrganizationUnits,
		updateCategoryNum,
		batchUpdateCategoryNums
	}
})

// category :: create category
export interface CreateCategoryRequest {
	company_id: number | null
	parent_id?: number | null
	title_en?: string
	title_tr?: string
	title_fa?: string
	content_en?: string
	content_tr?: string
	content_fa?: string
	staff_number_prefix?: string
	supervisor_id?: number | null
	file_id?: number | null
	image_id?: number | null
	mobile_image_id?: number | null
	background_image_id?: number | null
	mobile_background_image_id?: number | null
	icon_id?: number | null
	penalty_point?: number
	penalty_need_management_attention?: boolean
	verbal_warning?: number
	written_warning?: number
	penalty_message?: string
	type: string
	slug: string
	color?: string | null
}
export interface CreateCategoryResponse extends ApiResponse {
	data: Category
}
const CreateCategory = {
	method: "POST" as const,
	url: () => `categories/create`
}

// category :: edit category
export interface EditCategoryRequest {
	id: number
	company_id: number | null
	parent_id?: number | null
	title_en?: string
	title_tr?: string
	title_fa?: string
	content_en?: string
	content_tr?: string
	content_fa?: string
	staff_number_prefix?: string
	supervisor_id?: number | null
	file_id?: number | null
	image_id?: number | null
	mobile_image_id?: number | null
	background_image_id?: number | null
	mobile_background_image_id?: number | null
	icon_id?: number | null
	penalty_point?: number
	penalty_need_management_attention?: boolean
	verbal_warning?: number
	written_warning?: number
	penalty_message?: string
	slug: string
	color?: string | null
}
export interface EditCategoryResponse extends ApiResponse {
	data: Category
}
const EditCategory = {
	method: "POST" as const,
	url: () => `categories/edit`
}

// category :: delete category
export interface DeleteCategoryRequest {
	id: number
	company_id?: number
}
export interface DeleteCategoryResponse extends ApiResponse {
	data: null
}
const DeleteCategory = {
	method: "POST" as const,
	url: (id: any) => `categories/delete/${id}`
}

// category :: search categories
export interface SearchCategoriesRequest {
	type?: string
	company_id?: number | null
	parent_id?: number | null
	search?: string | null
	limit?: number | null
	except_category_id?: number | null
	page?: number | null
	order_by?: "updated" | "created"
	direction?: "asc" | "desc"
	sort_by_num?: boolean
}
export interface SearchCategoriesResponse extends ApiResponse {
	data: {
		categories: Category[]
		total: number
	}
}
export const SearchCategories = {
	method: "GET" as const,
	url: () => `categories/search`
}

// category :: get category
export interface GetCategoryRequest {}
export interface GetCategoryResponse extends ApiResponse {
	data: Category
}
const GetCategory = {
	method: "GET" as const,
	url: (id: any) => `categories/${id}`
}

// category :: create category excel
export interface CreateCategoryExcelRequest {
	company_id: number | null
	type: string
	file: File
	[key: string]: any
}
export interface CreateCategoryExcelResponse extends ApiResponse {
	data: {
		added: number
		updated: number
		organization_units: Category[]
	}
}
const CreateCategoryExcel = {
	method: "POST" as const,
	url: () => "categories/create-excel"
}

// category :: update num
export interface UpdateCategoryNumRequest {
	id: number
	company_id: number | null
	num: number | null
}
export interface UpdateCategoryNumResponse extends ApiResponse {
	data: Category
}
const UpdateCategoryNum = {
	method: "POST" as const,
	url: (id: number) => `categories/update-num/${id}`
}

// category :: batch update nums
export interface BatchUpdateCategoryNumsRequest {
	company_id: number | null
	items: { id: number; num: number | null }[]
}
const BatchUpdateCategoryNums = {
	method: "POST" as const,
	url: () => `categories/batch-update-nums`
}

export const useCategoryApiUrl = () => ({
	CreateCategory,
	EditCategory,
	DeleteCategory,
	SearchCategories,
	GetCategory,
	CreateCategoryExcel,
	UpdateCategoryNum,
	BatchUpdateCategoryNums
})
